<template>
  <section>
    <h2 class="section-title">포트폴리오 제작용 프로필 선택</h2>
    <div class="profile-box">
      <div class="col1" v-if="artist">
        <div class="inner">
          <dl>
            <dt>학력</dt>
            <dd>
            <span v-for="edu in visibleEducations" :key="edu.value" class="artist-profile-key">
              <ToggleSwitch v-model="fieldVisibility[edu.value]"/>
              <p class="artist-profile-key-text"
                 :class="[{'disabled-text': !fieldVisibility[edu.value]}, { 'selected-key': selectedProfileKey === edu.value }]"
                 @click="showProfileListData(groupedEducations,edu.value)">{{ edu.label }}</p>
            </span>
            </dd>

            <dt>경력</dt>
            <dd>
            <span v-for="exp in visibleExperiences" :key="exp.value" class="artist-profile-key">
              <ToggleSwitch v-model="fieldVisibility[exp.value]"/>
              <p class="artist-profile-key-text"
                 :class="[{'disabled-text': !fieldVisibility[exp.value]}, { 'selected-key': selectedProfileKey === exp.value }]"
                 @click="showProfileListData(groupedExperiences,exp.value)">{{ exp.label }}</p>
            </span>
            </dd>

            <dt>기타</dt>
            <dd>
              <span class="artist-profile-key" v-if="artist.awards.length">
                <ToggleSwitch v-model="fieldVisibility.awards"/>
                <p class="artist-profile-key-text"
                   :class="[{'disabled-text': !fieldVisibility.awards},{ 'selected-key': selectedProfileKey === 'awards' }]"
                   @click="showProfileListData(artist,'awards')">수상 내역</p>
              </span>
              <span class="artist-profile-key" v-if="artist.collections.length">
                <ToggleSwitch v-model="fieldVisibility.collections"/>
                <p class="artist-profile-key-text"
                   :class="[{'disabled-text': !fieldVisibility.collections},{ 'selected-key': selectedProfileKey === 'collections' }]"
                   @click="showProfileListData(artist,'collections')">작품 소장 내역</p>
              </span>
              <span class="artist-profile-key">
                <ToggleSwitch v-model="fieldVisibility.activityGenreTypes"/>
                <p class="artist-profile-key-text"
                   :class="[{'disabled-text': !fieldVisibility.activityGenreTypes},{ 'selected-key': selectedProfileKey === 'activityGenreTypes' }]"
                   @click="showProfileListData(artist,'activityGenreTypes')">활동 장르</p>
              </span>
              <span class="artist-profile-key">
                <ToggleSwitch v-model="fieldVisibility.artistNote"/>
                <p class="artist-profile-key-text"
                   :class="[{'disabled-text': !fieldVisibility.artistNote}]"
                   @click="showMessage">작가 노트</p>
              </span>
            </dd>
          </dl>
        </div>
      </div>
      <div ref="col2" class="col2">
        <div class="inner" :class="{'disabled-field': !!proflieListDataBykey?.key && !fieldVisibility[proflieListDataBykey.key]}">
          <dl>
            <dt class="profile-list-data-title-wrap">
              <h3>{{ proflieListDataBykey.title }}</h3>
              <label class="profile-list-data-checkbox" v-show="proflieListDataBykey.title">
                <input
                    type="checkbox"
                    :checked="isAllSelected"
                    @change="toggleAllSelected"
                />
                <span class="checkmark"></span>
                <span class="select-all-label">전체 선택</span>
              </label>
            </dt>
            <dd>
              <ul>
                <li v-for="(item, idx) in proflieListDataBykey.data" :key="idx" class="profile-list-data">
                  <label class="profile-list-data-checkbox">
                    <input type="checkbox" v-model="item.selected"/>
                    <span class="checkmark"></span>
                    <div class="artist-profile-value-text item-values">
                      <!-- 첫 번째 필드 -->
                      <span class="field first" :class="{'disabled-text': !item.selected}" v-if="getVisibleFields(item).length">
                        {{
                          proflieListDataBykey.key !== 'activityGenreTypes' ? getVisibleFields(item)[0][1] : printActivityGenre(
                              getVisibleFields(item)[0][1])
                        }}
                      </span>
                      <!-- 나머지 필드 -->
                      <div class="rest">
                        <span class="field" :class="{'disabled-text': !item.selected}" v-for="([key, value], i) in getVisibleFields(item).slice(1)"
                              :key="key + '-' + i">
                          {{ key !== 'status' ? value : getLabelFromType(ARTIST_EDUCATIONS_STATUS, value) }}
                        </span>
                      </div>
                    </div>

                  </label>
                </li>
              </ul>
            </dd>
          </dl>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import {onMounted, ref, computed, watch, nextTick, onBeforeUnmount} from "vue";
import {ARTIST_EXPERIENCES, ARTIST_ACTIVITY_GENRES, ARTIST_EDUCATIONS, ARTIST_EDUCATIONS_STATUS} from "@/common/common-enum";
import ToggleSwitch from "@/components/ui/ToggleSwitch.vue";
import {getLabelFromType} from "@/common/common-function";

const props = defineProps({
  fieldVisibility: Object,
  artist: Object,
  reload: Boolean,
});

const artist = ref(null)
const fieldVisibility = ref(null);
const proflieListDataBykey = ref({});
const selectedProfileKey = ref(null);

watch(
    () => props.reload,
    (newVal) => {
      if (newVal) {
        proflieListDataBykey.value = {};
        firstArtistKeyClick();
      }
    },
);

watch(
    () => props.artist,
    (newVal) => {
      artist.value = newVal;
    },
    {deep: true, immediate: true}
);
watch(
    () => props.fieldVisibility,
    (newVal) => {
      fieldVisibility.value = newVal;
    },
    {deep: true, immediate: true}
);

const groupByType = (list) => {
  return list.reduce((acc, item) => {
    const type = item.type
    if (!acc[type]) {
      acc[type] = []
    }
    acc[type].push(item)
    return acc
  }, {})
}

const groupedExperiences = computed(() => groupByType(artist.value.experiences))
const visibleExperiences = computed(() =>
    ARTIST_EXPERIENCES.filter(edu => groupedExperiences.value[edu.value]?.length)
)

const groupedEducations = computed(() => groupByType(artist.value.educations))
const visibleEducations = computed(() =>
    ARTIST_EDUCATIONS.filter(edu => groupedEducations.value[edu.value]?.length)
)

const isAllSelected = computed(() =>
    proflieListDataBykey.value.data?.length > 0 &&
    proflieListDataBykey.value.data.every(item => item.selected)
);

const toggleAllSelected = () => {
  const newValue = !isAllSelected.value;
  proflieListDataBykey.value.data?.forEach(item => {
    item.selected = newValue;
  });
};

const showProfileListData = async (group, key) => {
  const groupData = proflieListDataBykey.value;
  await checkAndDisableFieldVisibility(groupData);
  selectedProfileKey.value = key;
  const config = PROFILE_LIST_DATA_MAP[key];
  proflieListDataBykey.value.key = key;
  proflieListDataBykey.value.title = config.title(key);
  const data = group[key];
  proflieListDataBykey.value.data = Array.isArray(data) ? data : [data];
}

const PROFILE_LIST_DATA_MAP = {
  GRADUATE: {
    title: (value) => getLabelFromType(ARTIST_EDUCATIONS, value),
  },
  UNDERGRADUATE: {
    title: (value) => getLabelFromType(ARTIST_EDUCATIONS, value),
  },
  ETC: {
    title: (value) => getLabelFromType(ARTIST_EDUCATIONS, value),
  },
  SOLO_EXHIBITION: {
    title: (value) => getLabelFromType(ARTIST_EXPERIENCES, value),
  },
  GROUP_EXHIBITION: {
    title: (value) => getLabelFromType(ARTIST_EXPERIENCES, value),
  },
  RESIDENCY: {
    title: (value) => getLabelFromType(ARTIST_EXPERIENCES, value),
  },
  WORKSHOP: {
    title: (value) => getLabelFromType(ARTIST_EXPERIENCES, value),
  },
  GRANT: {
    title: (value) => getLabelFromType(ARTIST_EXPERIENCES, value),
  },
  awards: {
    title: (value) => '수상 내역',
  },
  collections: {
    title: (value) => '작품 소장 내역',
  },
  activityGenreTypes: {
    title: (value) => '활동 장르',
  },
}

const showMessage = () => {
  alert('작가 노트는 포트폴리오 미리보기 화면에서 입력할 수 있습니다.');
}

function getVisibleFields(item) {
  return Object.entries(item).filter(([key]) => key !== 'selected' && key !== 'type');
}

const printActivityGenre = (type) => {
  return type !== 'ETC' ? getLabelFromType(ARTIST_ACTIVITY_GENRES, type) : artist.value.activityGenreEtc
}

function firstArtistKeyClick() {
  const firstEl = document.querySelector('.artist-profile-key-text');
  if (firstEl) {
    firstEl.click();
  }
}

onMounted(async () => {
  document.addEventListener('click', handleClickOutside);
  await nextTick(() => {
    firstArtistKeyClick();
  });
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});

const checkAndDisableFieldVisibility = async (groupData) => {
  const isAllFalse = groupData.data?.map(item => item.selected).every(selected => !selected);
  const key = groupData.key;
  if (key && isAllFalse) {
    fieldVisibility.value[key] = false;
  }
}

const col2 = ref(null);
const handleClickOutside = (event) => {
  // col2 영역 외부를 클릭했을 때 실행
  if (col2.value && !col2.value.contains(event.target)) {
    checkAndDisableFieldVisibility(proflieListDataBykey.value);
  }
};

</script>

<style scoped>


</style>
